import { SettingsDialog } from "./components/settings/SettingsDialog";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useCallback, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuthStore } from "./store/auth";

import { Toaster } from "sonner";
import { CommandPalette } from "./components/CommandPalette";
import { ExportProgressDialog } from "./components/ExportProgressDialog";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { PaywallDialog } from "./components/PaywallDialog";
import { OnboardingGate } from "./components/onboarding/OnboardingOverlay";
import { WhatsNewGate } from "./components/WhatsNewDialog";
import { ProThanksDialog } from "./components/ProThanksDialog";
import { useCreateNote } from "./hooks/useNotes";
import { useNoteLimitProfile } from "./hooks/useNoteLimit";
import { useSubscriptionSync } from "./hooks/useSubscriptionSync";
import { usePushUnregister } from "./hooks/usePushUnregister";
import { useGlobalShortcut } from "./hooks/useShortcuts";
import { useSync } from "./hooks/useSync";
import { useTags } from "./hooks/useTags";
import { useEditorTypography } from "./hooks/useEditorTypography";
import { useFonts } from "./hooks/useFonts";
import { useTheme } from "./hooks/useTheme";
import { getNote } from "./lib/db";
import { setPrefetchedNote } from "./lib/notePrefetch";
import { newNoteContent, newNoteTagPath } from "./lib/newNoteContent";
import { extractTitleFromContent } from "./hooks/useEditorAutosave";
import { useUIStore } from "./store/ui";

const InvitePage = lazy(() =>
  import("./pages/InvitePage").then((m) => ({ default: m.InvitePage })),
);
const AppPage = lazy(() =>
  import("./pages/AppPage").then((m) => ({ default: m.AppPage })),
);
const AuthPage = lazy(() =>
  import("./pages/AuthPage").then((m) => ({ default: m.AuthPage })),
);
const SharePage = lazy(() =>
  import("./pages/SharePage").then((m) => ({ default: m.SharePage })),
);
const ForgotPasswordPage = lazy(() =>
  import("./components/auth/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./components/auth/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const ApprovePage = lazy(() =>
  import("./components/connectors/ApprovePage").then((m) => ({
    default: m.ApprovePage,
  })),
);

function AppRoutes() {
  const { checkAuth, user, localMode } = useAuthStore();
  const createNote = useCreateNote();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tags = useTags();
  const creatingRef = useRef(false);
  const location = useLocation();
  const isPublicRoute =
    location.pathname.startsWith("/share/") ||
    location.pathname.startsWith("/connectors/") ||
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/verify-email";

  // Smart sync: polls server, adapts to tab visibility, syncs on focus/reconnect
  useSync();

  // Once per device: remove the Web Push registration (the app no longer
  // uses push).
  usePushUnregister(!!user);

  // Single writer of the `note_limit` Mixpanel profile property.
  useNoteLimitProfile();

  useEffect(() => {
    if (!user) {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global shortcuts pause while the first-run onboarding overlay is up so
  // the palette / a new note can't open underneath it.
  const onboardingOpen = useUIStore((s) => s.onboardingOpen);

  // Global shortcut: Command Palette
  const openCmdPalette = useCallback(() => {
    useUIStore.getState().setCmdPaletteOpen(true);
  }, []);
  useGlobalShortcut(
    "app.commandPalette",
    openCmdPalette,
    !isPublicRoute && !onboardingOpen,
  );

  // Global shortcut: Settings (⌘, like every Mac app).
  const openSettings = useCallback(() => useUIStore.getState().openSettings(), []);
  useGlobalShortcut("app.settings", openSettings, !isPublicRoute && !onboardingOpen);

  // Global shortcut: New Note — local mode writes to the local DB like any
  // signed-in session, so the shortcut works without a user.
  const createNoteHandler = useCallback(async () => {
    if (creatingRef.current || (!user && !localMode)) return;
    creatingRef.current = true;
    try {
      const tagId = searchParams.get("tag");
      const content = newNoteContent(
        useUIStore.getState().newNoteStart,
        newNoteTagPath(tagId, tags),
        new Date(),
      );
      const note = await createNote.mutateAsync(
        {
          title: extractTitleFromContent(content),
          content,
          tags: tagId ? [tagId] : undefined,
        },
        "shortcut",
      );
      // null: the free note limit is reached and the paywall is already open.
      if (!note) return;
      useUIStore.getState().setPendingNewNoteId(note.id);
      const localNote = getNote(note.id);
      if (localNote) setPrefetchedNote(localNote);
      const qs = searchParams.toString();
      navigate(`/app/note/${note.id}${qs ? `?${qs}` : ""}`);
    } finally {
      creatingRef.current = false;
    }
  }, [user, localMode, createNote, navigate, searchParams, tags]);
  useGlobalShortcut(
    "app.newNote",
    createNoteHandler,
    !isPublicRoute && (!!user || localMode) && !onboardingOpen,
  );

  return (
    <Suspense>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/app" replace />} />

        {/* Auth */}
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public share viewer */}
        <Route path="/share/:id" element={<SharePage />} />

        {/* Remote MCP connector consent */}
        <Route path="/connectors/approve" element={<ApprovePage />} />

        {/* App (protected) */}
        <Route path="/app" element={<AppPage />} />
        <Route path="/app/note/:id" element={<AppPage />} />
        {/* An invite email's link (PLAN.md task 3.5) */}
        <Route path="/app/invite/:token" element={<InvitePage />} />
        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  const { t } = useTranslation();
  useTheme();
  useFonts();
  useEditorTypography();
  useSubscriptionSync();

  return (
    <BrowserRouter>
      <AppRoutes />
      <CommandPalette />
      <SettingsDialog />
      <OnboardingGate />
      <WhatsNewGate />
      <PaywallDialog />
      <ProThanksDialog />
      <ExportProgressDialog />
      <UpdatePrompt />
      <Toaster
        position="bottom-center"
        // Some toasts stay until dismissed (new version, local-mode notice):
        // list a stack in full instead of tucking toasts behind each other.
        expand
        toastOptions={{
          closeButtonAriaLabel: t("common.close"),
          // One look for every toast: a card in the theme's own colors (light
          // in light mode), text left, actions and the close button inline at
          // the end. `!` beats Sonner's own styles.
          classNames: {
            toast:
              "!items-center !gap-3 !rounded-2xl !border !border-border !bg-canvas !py-2.5 !pl-4 !pr-2 !font-chrome !text-sm !text-ink !shadow-lg",
            title: "!font-normal !leading-snug",
            // No icons: the text says it (calmer, and every toast reads the same).
            icon: "!hidden",
            closeButton:
              "!static !order-last !size-7 !shrink-0 !transform-none !rounded-full !border-0 !bg-transparent !text-muted hover:!bg-hover hover:!text-ink",
            success: "caliu-toast-success",
            error: "caliu-toast-error",
            // A toast's action (e.g. Reload, Sign in) is an accent pill, like
            // the app's primary small Button. `!` beats Sonner's own styles.
            actionButton:
              "!m-0 !ml-auto !h-7 !shrink-0 !self-center !rounded-full !bg-accent !px-3.5 !text-[13px] !font-semibold !text-on-accent hover:!bg-accent-hover",
          },
        }}
      />
    </BrowserRouter>
  );
}
